import { ChevronLeft, ChevronRight, Plus, ChevronDown, ChevronUp } from "lucide-react";
import { format, addMonths, subMonths, startOfMonth, endOfMonth, eachDayOfInterval, getDay, isSameMonth, isSameDay, isToday } from "date-fns";
import { Button } from "../ui/button";
import { useState } from "react";

export type CalendarItem = {
  id: string;
  label: string;
  color: string;
  checked: boolean;
};

type CalendarSidebarProps = {
  currentDate: Date;
  selectedDate: Date | null;
  onSelectDate: (date: Date) => void;
  onNavigateMonth: (dir: -1 | 1) => void;
  hasEventsOnDate: (date: Date) => boolean;
  myCalendars: CalendarItem[];
  onCalendarToggle: (id: string, checked: boolean) => void;
  categories: { id: string; label: string; color: string }[];
  selectedCategoryId: string | null;
  onCategorySelect: (id: string | null) => void;
  onAddSchedule: () => void;
  onAddWorker?: () => void;
};

export function CalendarSidebar({
  currentDate,
  selectedDate,
  onSelectDate,
  onNavigateMonth,
  hasEventsOnDate,
  myCalendars,
  onCalendarToggle,
  categories,
  selectedCategoryId,
  onCategorySelect,
  onAddSchedule,
  onAddWorker,
}: CalendarSidebarProps) {
  const [myCalendarsOpen, setMyCalendarsOpen] = useState(true);
  const [otherCalendarsOpen, setOtherCalendarsOpen] = useState(false);
  const [categoriesOpen, setCategoriesOpen] = useState(true);

  const monthStart = startOfMonth(currentDate);
  const monthEnd = endOfMonth(currentDate);
  const days = eachDayOfInterval({ start: monthStart, end: monthEnd });
  const startDay = getDay(monthStart);
  const calendarDays = Array(startDay).fill(null).concat(days);

  return (
    <aside className="w-full max-w-[280px] flex-shrink-0 flex flex-col rounded-xl border border-[var(--dash-border)] bg-[var(--dash-card)] shadow-[var(--dash-shadow)] overflow-hidden h-fit">
      {/* Mini calendar */}
      <div className="p-4 border-b border-[var(--dash-border)]">
        <div className="flex items-center justify-between mb-3">
          <span className="text-sm font-semibold text-[var(--dash-text)]">
            {format(currentDate, "MMMM yyyy")}
          </span>
          <div className="flex items-center gap-0.5">
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8"
              onClick={() => onNavigateMonth(-1)}
              aria-label="Previous month"
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8"
              onClick={() => onNavigateMonth(1)}
              aria-label="Next month"
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
        <div className="grid grid-cols-7 gap-0.5 text-center">
          {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((d) => (
            <div key={d} className="text-[10px] font-medium text-[var(--dash-text-muted)] py-0.5">
              {d.slice(0, 1)}
            </div>
          ))}
          {calendarDays.map((day, i) => {
            if (!day) {
              return <div key={`empty-${i}`} className="aspect-square" />;
            }
            const isSelected = selectedDate && isSameDay(day, selectedDate);
            const hasEvents = hasEventsOnDate(day);
            const isCurrentMonth = isSameMonth(day, currentDate);
            return (
              <button
                key={day.toISOString()}
                type="button"
                onClick={() => onSelectDate(day)}
                className={`
                  aspect-square rounded-full text-xs font-medium transition flex flex-col items-center justify-center gap-0.5
                  ${!isCurrentMonth ? "text-[var(--dash-text-muted)] opacity-60" : "text-[var(--dash-text)]"}
                  ${isSelected ? "bg-blue-500 text-white ring-2 ring-blue-400 ring-offset-2 ring-offset-[var(--dash-card)]" : ""}
                  ${!isSelected && isToday(day) ? "bg-[var(--dash-accent)] text-white" : ""}
                  ${!isSelected && !isToday(day) ? "hover:bg-[var(--dash-bg)]" : ""}
                `}
              >
                <span>{format(day, "d")}</span>
                {hasEvents && !isSelected && (
                  <span className="w-1 h-1 rounded-full bg-[var(--dash-accent)]" aria-hidden />
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* Scheduled */}
      <div className="px-4 py-2 border-b border-[var(--dash-border)] flex items-center justify-between">
        <span className="text-sm font-medium text-[var(--dash-text)]">Scheduled</span>
        <Button variant="ghost" size="icon" className="h-8 w-8" onClick={onAddSchedule} aria-label="Add schedule">
          <Plus className="h-4 w-4" />
        </Button>
      </div>

      {/* My Calendars */}
      <div className="px-4 py-2">
        <button
          type="button"
          onClick={() => setMyCalendarsOpen(!myCalendarsOpen)}
          className="flex items-center justify-between w-full text-sm font-medium text-[var(--dash-text)] hover:opacity-80"
        >
          My Calendars
          {myCalendarsOpen ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
        </button>
        {myCalendarsOpen && (
          <ul className="mt-2 space-y-1">
            {myCalendars.map((cal) => (
              <li key={cal.id} className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id={`cal-${cal.id}`}
                  checked={cal.checked}
                  onChange={(e) => onCalendarToggle(cal.id, e.target.checked)}
                  className="rounded border-[var(--dash-border)] text-[var(--dash-accent)] focus:ring-[var(--dash-accent)]"
                />
                <span
                  className="w-3 h-3 rounded-sm flex-shrink-0"
                  style={{ backgroundColor: cal.color }}
                  aria-hidden
                />
                <label htmlFor={`cal-${cal.id}`} className="text-sm text-[var(--dash-text)] cursor-pointer truncate flex-1">
                  {cal.label}
                </label>
              </li>
            ))}
            {onAddWorker && (
              <li>
                <button type="button" onClick={onAddWorker} className="text-xs text-[var(--dash-accent)] hover:underline mt-1">
                  + Add other
                </button>
              </li>
            )}
          </ul>
        )}
      </div>

      {/* Other Calendars (collapsible, empty for now) */}
      <div className="px-4 py-2 border-t border-[var(--dash-border)]">
        <button
          type="button"
          onClick={() => setOtherCalendarsOpen(!otherCalendarsOpen)}
          className="flex items-center justify-between w-full text-sm font-medium text-[var(--dash-text-muted)] hover:opacity-80"
        >
          Other Calendars
          {otherCalendarsOpen ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
        </button>
      </div>

      {/* Categories */}
      <div className="px-4 py-2 border-t border-[var(--dash-border)]">
        <button
          type="button"
          onClick={() => setCategoriesOpen(!categoriesOpen)}
          className="flex items-center justify-between w-full text-sm font-medium text-[var(--dash-text-muted)] hover:opacity-80"
        >
          Categories
          {categoriesOpen ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
        </button>
        {categoriesOpen && categories.length > 0 && (
          <ul className="mt-2 space-y-1">
            {categories.map((cat) => {
              const isCalendar = cat.id === "calendar";
              const isSelected = isCalendar ? selectedCategoryId !== "projects" : selectedCategoryId === cat.id;
              const handleClick = () => {
                if (isCalendar) onCategorySelect(null);
                else onCategorySelect(isSelected ? null : cat.id);
              };
              return (
                <li key={cat.id}>
                  <button
                    type="button"
                    onClick={handleClick}
                    className={`flex items-center gap-2 text-sm w-full text-left rounded-md px-2 py-1.5 transition ${
                      isSelected ? "bg-[var(--dash-accent)]/20 text-[var(--dash-accent)]" : "text-[var(--dash-text)] hover:bg-[var(--dash-bg)]"
                    }`}
                  >
                    <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: cat.color }} aria-hidden />
                    {cat.label}
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </aside>
  );
}
